import SignalingService from "./SignalingService";
import PeerConnectionManager from "./PeerConnectionManager";
import ConnectionManager from "./ConnectionManager";
import { RequestManager } from "./RequestManager";
import IceServersConfigService from "./IceServersConfigService";
import MediaTrackService from "./MediaTrackService";
import CustomErrorManager from "common/services/CustomErrorManager.js";
import CustomErrorCodeMessages from "common/enums/CustomErrorMessages.js";
import CustomErrorCodes from "common/enums/CustomErrorCodes.js";

import { USER_ROLE, MEDIA_TYPE, AUTHENTICATION_TYPE } from "./enums/LiveStreamingEnums";
import LoggerService from "./LoggerService";

export default class LiveStreaming {
  static signalingService;
  static isInitializingSocket = false;
  static config = {
    userRole: USER_ROLE.VIEWER
  };
  // please read from APIconstants
  static WEBSOCKET_LIST = {
    LIVE_STREAMING_API_V2: "communication-v2",
    MANUAL_PROCTORING_API_HUB_V2: "manual-proctoring-v2",
    COMMUNICATION_API_API_HUB_V2: "communication-v2"
  };

  static init(config, callbacks) {
    if (!config.userId || !config.websocketConnectionURL || config.userRole == undefined) {
      throw CustomErrorManager.ValidationError(
        CustomErrorCodes.CONFIG_MISSING_REQUIRED_PARAMETER,
        CustomErrorCodeMessages.CONFIG_MISSING_REQUIRED_PARAMETER
      );
    }

    if (![USER_ROLE.BROADCASTER, USER_ROLE.VIEWER].includes(config.userRole)) {
      throw CustomErrorManager.ValidationError(
        CustomErrorCodes.CONFIG_VALIDATION_FAILED,
        "userRole paramter is invalid"
      );
    }

    if (config.userRole == USER_ROLE.VIEWER && config.launchId == undefined) {
      throw CustomErrorManager.ValidationError(
        CustomErrorCodes.CONFIG_MISSING_REQUIRED_PARAMETER,
        "launchId paramter is required"
      );
    }

    if (![AUTHENTICATION_TYPE.SESSION, AUTHENTICATION_TYPE.SDK].includes(config.authType)) {
      throw CustomErrorManager.ValidationError(
        CustomErrorCodes.CONFIG_MISSING_REQUIRED_PARAMETER,
        "authType paramter is invalid"
      );
    }

    if (typeof config.websocketConnectionURL != "string") {
      throw CustomErrorManager.ValidationError(
        CustomErrorCodes.CONFIG_VALIDATION_FAILED,
        "websocketConnectionURL paramter is invalid"
      );
    }

    if (config.authType == AUTHENTICATION_TYPE.SDK) {
      if (typeof callbacks == "object" && typeof callbacks.getTurnServerConfig == "function") {
        LiveStreaming.getTurnServerConfig = callbacks.getTurnServerConfig;
      } else {
        throw CustomErrorManager.ValidationError(
          CustomErrorCodes.CONFIG_MISSING_REQUIRED_PARAMETER,
          "getTurnServerConfig callback method is not provided"
        );
      }

      if (
        typeof callbacks == "object" &&
        typeof callbacks.getLiveStreamingWebSocketToken == "function"
      ) {
        LiveStreaming.getLiveStreamingWebSocketToken = callbacks.getLiveStreamingWebSocketToken;
      } else {
        throw CustomErrorManager.ValidationError(
          CustomErrorCodes.CONFIG_MISSING_REQUIRED_PARAMETER,
          "getLiveStreamingWebSocketToken callback method is not provided"
        );
      }
    }

    LiveStreaming.config = { ...config };
    SignalingService.init(LiveStreaming.config.websocketConnectionURL);

    IceServersConfigService.init(LiveStreaming.config);

    if (LiveStreaming.config.userRole == USER_ROLE.VIEWER) {
      RequestManager.init();

      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[Streaming] [INIT] [userID: ${LiveStreaming.config.userId}] [lunchId: ${LiveStreaming.config.launchId}]`
      );
    } else {
      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[Streaming] [INIT] [userID]: ${LiveStreaming.config.userId}]`
      );
    }
  }

  static async start() {
    if (LiveStreaming.isInitializingSocket) return; // Prevent duplicate execution
    LiveStreaming.isInitializingSocket = true; // Set busy flag

    try {
      LiveStreaming.signalingService = SignalingService.getInstance();
      await LiveStreaming.signalingService.start();
      await LiveStreaming.joinRoom(LiveStreaming.config.userId);
      await LiveStreaming.addListeners();
      await LiveStreaming.addSocketListeners();

      if (LiveStreaming.config.userRole === USER_ROLE.VIEWER) {
        RequestManager.start();
      }

      LoggerService.info(
        LoggerService.MODULE.STREAMING,
        `[Streaming] [STARTED]`
      );

    } catch (error) {
      LoggerService.error(
        LoggerService.MODULE.STREAMING,
        `[Streaming] [Failed to START]`,
        error
      );
      throw error;
    } finally {
      LiveStreaming.isInitializingSocket = false; // Reset busy flag
    }
  }

  static async stop() {
    await LiveStreaming.leaveRoom(LiveStreaming.config.userId);
    await LiveStreaming.removeListeners();
    await LiveStreaming.signalingService?.destroy();

    if (LiveStreaming.config.userRole == USER_ROLE.VIEWER) {
      RequestManager.stop();
    }

    LoggerService.info(
      LoggerService.MODULE.STREAMING,
      `[Streaming] [STOPPED]`
    );
  }

  static async reset() {
    await RequestManager.stop();
    await RequestManager.start();
  }

  static async addListeners() {
    if (LiveStreaming.config.userRole == USER_ROLE.BROADCASTER) {
      await LiveStreaming.signalingService.listenToMediaStreamingStartRequest();
      await LiveStreaming.signalingService.listenToMediaStreamingStopRequest();
      await LiveStreaming.signalingService.listenToPeerAnswer();
    }

    if (LiveStreaming.config.userRole == USER_ROLE.VIEWER) {
      await LiveStreaming.signalingService.listenToPeerOffer();
    }

    await LiveStreaming.signalingService.listenToIceCandidates();
  }

  static async removeListeners() {
    if (LiveStreaming.config.userRole == USER_ROLE.BROADCASTER) {
      await LiveStreaming.signalingService?.removeMediaStreamingStartRequestListener();
      await LiveStreaming.signalingService?.removeMediaStreamingStopRequestListener();
      await LiveStreaming.signalingService?.removePeerAnswerListener();
    }

    if (LiveStreaming.config.userRole == USER_ROLE.VIEWER) {
      await LiveStreaming.signalingService?.removePeerOfferListener();
    }

    await LiveStreaming.signalingService?.removeIceCandidatesListener();
  }

  static async onReconnecting() {
    ConnectionManager.currentUserDisconnected();
  }

  static async onReconnected() {
    ConnectionManager.currentUserReconnected();

    if (LiveStreaming.config.userRole == USER_ROLE.BROADCASTER) {
      await LiveStreaming.joinRoom(LiveStreaming.config.userId);
    }
  }

  static async onDisconnected() {
    ConnectionManager.currentUserDisconnected();
  }

  static async addSocketListeners() {
    await LiveStreaming.signalingService.onReconnecting(LiveStreaming.onReconnecting);
    await LiveStreaming.signalingService.onReconnected(LiveStreaming.onReconnected);
    await LiveStreaming.signalingService.onDisconnected(LiveStreaming.onDisconnected);
  }

  static async joinRoom(roomId) {
    await LiveStreaming.signalingService.joinRoom(roomId);
  }

  static async leaveRoom(roomId) {
    await LiveStreaming.signalingService?.leaveRoom(roomId);
  }

  static async sendMediaStartRequest(userId, requestedStreams) {
    try {
      if (userId == null) {
        throw new Error("user id is required");
      }

      if (requestedStreams == null || typeof requestedStreams != "object") {
        throw new Error("requested streams are not provided in the correct format");
      }

      const config = {
        userId,
        requestedStreams
      };

      await RequestManager.createNewRequest(config);

    } catch (error) {
      LoggerService.error(
        LoggerService.MODULE.STREAMING,
        `[Streaming] Failed to send Media start request`,
        error
      );
      throw error;
    }
  }

  static async sendMediaStopRequest(peerId) {
    try {
      if (peerId == null) {
        throw new Error("peer id is required");
      }

      const { userId, requestedStreams } = LiveStreaming.getDataFromPeerId(peerId);

      const config = {
        userId,
        requestedStreams
      };

      await RequestManager.closeRequest(config);
    } catch (error) {
      LoggerService.error(
        LoggerService.MODULE.STREAMING,
        `[Streaming] Failed to send Media stop request`,
        error
      );
      throw error;
    }
  }

  static getLiveStreamingMapping() {
    return PeerConnectionManager.getLiveStreamingMapping();
  }

  static getActiveRequests() {
    return RequestManager.allRequests;
  }

  static addWebcamStream(stream) {
    MediaTrackService.setLocalWebcamStream(stream);
  }

  static addScreenshareStream(stream) {
    MediaTrackService.setLocalScreenshareStream(stream);
  }

  static async removeWebcamStream() {
    MediaTrackService.setLocalWebcamStream(null);

    // destroy all connections where webcam stream was requested
    for (const peerId in PeerConnectionManager.peerConnectionMap) {
      let peerConnection = PeerConnectionManager.getPeerConnection(peerId);

      if (peerConnection) {
        let isWebcamStreamRequested = peerConnection.requestedStreams.webcam;

        if (isWebcamStreamRequested) {
          PeerConnectionManager.removePeerConnection(peerId);
        }
      }
    }
  }

  static async removeScreenshareStream() {
    MediaTrackService.setLocalScreenshareStream(null);

    // destroy all requests where screenshareStream was requested
    for (const peerId in PeerConnectionManager.peerConnectionMap) {
      let peerConnection = PeerConnectionManager.getPeerConnection(peerId);

      if (peerConnection) {
        let isScreenshareStreamRequested = peerConnection.requestedStreams.screen;

        if (isScreenshareStreamRequested) {
          PeerConnectionManager.removePeerConnection(peerId);
        }
      }
    }
  }

  static getLocalWebcamStream() {
    return MediaTrackService.getLocalWebcamStream();
  }

  static getLocalScreenshareStream() {
    return MediaTrackService.getLocalScreenshareStream();
  }

  static getRemoteStream(peerId) {
    return PeerConnectionManager.getRemoteStream(peerId);
  }

  static getConnectionState(peerId) {
    return PeerConnectionManager.getConnectionState(peerId);
  }

  static getPeerId(userId, mediaType) {
    const launchId = LiveStreaming.config.launchId;

    if (mediaType == MEDIA_TYPE.WEBCAM) {
      return userId + "|0|" + launchId;
    }
    if (mediaType == MEDIA_TYPE.SCREENSHARE) {
      return userId + "|1|" + launchId;
    }
  }

  static getDataFromPeerId(peerId) {
    let data = peerId.split("|");
    let requestedStreams = {
      webcam: false,
      screen: false
    };

    if (data[1].includes("0")) {
      requestedStreams.webcam = true;
    }

    if (data[1].includes("1")) {
      requestedStreams.screen = true;
    }

    return {
      userId: data[0],
      requestedStreams
    };
  }

  static getMediaType(requestedStreams) {
    if (requestedStreams.webcam) {
      return MEDIA_TYPE.WEBCAM;
    }

    if (requestedStreams.screen) {
      return MEDIA_TYPE.SCREENSHARE;
    }
  }

  static remoteUserReconnected(peerId) {
    ConnectionManager.remoteUserReconnected(peerId);
  }

  static remoteUserDisconnected(peerId) {
    ConnectionManager.remoteUserDisconnected(peerId);
  }

  static notifyActiveConnections() {
    PeerConnectionManager.notifyActiveConnections();
  }
}
